import React, { useMemo, useState } from "react";
import {
  AlertCircle, ArrowDownToLine, ArrowUpFromLine, Boxes, Loader2, Package, Plus,
  RefreshCw, Search, Trash2, AlertTriangle,
} from "lucide-react";
import StoreModal from "../components/StoreModal";
import MovementModal from "../components/MovementModal";
import { usePermission } from "../../../hooks/usePermission";
import { useAuth } from "../../../context/AuthContexts";
import StoreShell from "../components/StoreShell";
import {
  MovementType,
  useStoreCreateItemMutation,
  useStoreDeleteItemMutation,
  useStoreGetCategoriesQuery,
  useStoreGetItemQuery,
  useStoreGetItemsQuery,
  useStoreGetSummaryQuery,
  useStoreUpdateItemMutation,
} from "../api/storesApi";
import {
  errMsg, fmtDate, fmtNaira, fmtQty, movementColor, movementLabel, recipient, signedQty,
} from "../utils/stores";

const inputCls =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-xs font-medium text-gray-600 mb-1";

/** The item register: what the store holds, and the shortcuts to move it. */
export default function StoreItemsPage() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [moving, setMoving] = useState<{ item: any; type: MovementType } | null>(null);
  const [card, setCard] = useState<any | null>(null);

  // Opening this page needs store.access; the two things it can DO need their
  // own grants. super_admin is bypassed the same way RequirePermission does it,
  // so a role that holds everything is never locked out of a button.
  const { can } = usePermission();
  const { hasRole } = useAuth();
  const isSuper = hasRole("super_admin");
  const canManage = isSuper || can("store.manage");
  const canMove = isSuper || can("store.movement");

  const { data, isLoading, isFetching, refetch } = useStoreGetItemsQuery({});
  const { data: summary } = useStoreGetSummaryQuery();
  const { data: categories = [] } = useStoreGetCategoriesQuery();

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return rows.filter((r: any) => {
      if (category && r.category !== category) return false;
      if (lowOnly && !r.low_stock) return false;
      if (!nq) return true;
      return [r.name, r.code, r.category, r.location].filter(Boolean).join(" ").toLowerCase().includes(nq);
    });
  }, [rows, q, category, lowOnly]);

  return (
    <StoreShell>
    <div>
      <div className="bg-white border rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <Boxes className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Store Items</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Balances move only through receipts and issues — every figure here has a stock card behind it.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <button
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" /> New item
              </button>
            )}
            <button onClick={() => refetch()} disabled={isFetching} className="p-2 rounded-lg border hover:bg-gray-50" aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Headline figures */}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Items" value={summary?.items ?? rows.length} />
          <Stat label="Needs restocking" value={summary?.low_stock ?? 0} warn={(summary?.low_stock ?? 0) > 0} />
          <Stat label="Stock value" value={fmtNaira(summary?.stock_value)} />
          <Stat label="Issued this month" value={fmtQty(summary?.issued)} />
        </div>

        {/* Filters */}
        <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, code, category or location…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
          >
            <option value="">All categories</option>
            {(categories as string[]).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            onClick={() => setLowOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${
              lowOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Low stock
          </button>
        </div>

        {isLoading ? (
          <div className="py-10 flex justify-center text-gray-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            {rows.length === 0
              ? canManage
                ? "No items in the store yet — add the first one."
                : "No items in the store yet."
              : "No items match your filters."}
          </div>
        ) : (
          <div className="mt-5 border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                  <th className="px-4 py-2 font-medium">Item</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium text-right">In stock</th>
                  <th className="px-4 py-2 font-medium text-right">Reorder at</th>
                  <th className="px-4 py-2 font-medium text-right">Value</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r: any) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <button onClick={() => setCard(r)} className="font-medium text-gray-900 hover:underline text-left">
                        {r.name}
                      </button>
                      <div className="text-xs text-gray-400">
                        <span className="font-mono">{r.code}</span>
                        {r.location ? ` · ${r.location}` : ""}
                        {!r.active && " · inactive"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.category || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${r.low_stock ? "text-amber-600" : "text-gray-900"}`}>
                        {fmtQty(r.quantity)}
                      </span>
                      <span className="text-gray-400 text-xs"> {r.unit}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">{fmtQty(r.reorder_level)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{r.unit_cost ? fmtNaira(r.stock_value) : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {canMove && (
                          <>
                            <button
                              onClick={() => setMoving({ item: r, type: "in" })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-200 text-green-700 text-xs font-semibold hover:bg-green-50"
                            >
                              <ArrowDownToLine className="w-3.5 h-3.5" /> Receive
                            </button>
                            <button
                              onClick={() => setMoving({ item: r, type: "out" })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 text-xs font-semibold hover:bg-amber-50"
                            >
                              <ArrowUpFromLine className="w-3.5 h-3.5" /> Issue
                            </button>
                          </>
                        )}
                        {canManage && (
                          <button onClick={() => setEditing(r)} className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50">
                            Edit
                          </button>
                        )}
                        {!canMove && !canManage && (
                          <span className="text-xs text-gray-300">View only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(creating || editing) && (
        <ItemModal initial={editing} onClose={() => { setCreating(false); setEditing(null); }} />
      )}
      {moving && (
        <MovementModal type={moving.type} item={moving.item} onClose={() => setMoving(null)} />
      )}
      {card && <StockCardModal item={card} onClose={() => setCard(null)} />}
    </div>
    </StoreShell>
  );
}

function Stat({ label, value, warn = false }: { label: string; value: React.ReactNode; warn?: boolean }) {
  return (
    <div className={`px-4 py-3 rounded-xl border ${warn ? "bg-amber-50 border-amber-100" : "bg-gray-50"}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${warn ? "text-amber-700" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}

/* ── Item create / edit ───────────────────────────────────────────────────── */

function ItemModal({ initial, onClose }: { initial?: any | null; onClose: () => void }) {
  const { data: categories = [] } = useStoreGetCategoriesQuery();
  const [create, { isLoading: creating }] = useStoreCreateItemMutation();
  const [update, { isLoading: updating }] = useStoreUpdateItemMutation();
  const [remove, { isLoading: removing }] = useStoreDeleteItemMutation();

  const [form, setForm] = useState({
    name: initial?.name ?? "",
    code: initial?.code ?? "",
    category: initial?.category ?? "",
    unit: initial?.unit ?? "pcs",
    location: initial?.location ?? "",
    reorder_level: initial?.reorder_level ?? "",
    unit_cost: initial?.unit_cost ?? "",
    description: initial?.description ?? "",
    active: initial?.active ?? true,
    opening_quantity: "",
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: any = {
      name: form.name.trim(),
      code: form.code.trim(),
      category: form.category.trim() || null,
      unit: form.unit.trim() || "pcs",
      location: form.location.trim() || null,
      reorder_level: form.reorder_level === "" ? 0 : Number(form.reorder_level),
      unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
      description: form.description.trim() || null,
      active: !!form.active,
    };
    try {
      if (initial) {
        await update({ id: initial.id, body }).unwrap();
      } else {
        // Stock already on the shelf is posted as the item's opening receipt.
        if (form.opening_quantity !== "") body.opening_quantity = Number(form.opening_quantity);
        await create(body).unwrap();
      }
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const del = async () => {
    if (!initial) return;
    if (!window.confirm(`Remove ${initial.name}? Items that have been moved are deactivated instead.`)) return;
    setError(null);
    try {
      await remove(initial.id).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <StoreModal
      title={initial ? "Edit item" : "New store item"}
      icon={<Package className="w-4 h-4 text-indigo-600" />}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Name *</label>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. A4 Paper" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Code *</label>
            <input required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. STA-001" className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Category</label>
            <input
              list="store-categories"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              placeholder="e.g. Stationery"
              className={inputCls}
            />
            <datalist id="store-categories">
              {(categories as string[]).map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className={labelCls}>Unit of issue *</label>
            <input required value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="pcs, reams, cartons…" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Shelf / location</label>
            <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Reorder level</label>
            <input
              type="number" min={0} step="0.01"
              value={form.reorder_level}
              onChange={(e) => setForm((f) => ({ ...f, reorder_level: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Unit cost (₦)</label>
            <input
              type="number" min={0} step="0.01"
              value={form.unit_cost}
              onChange={(e) => setForm((f) => ({ ...f, unit_cost: e.target.value }))}
              className={inputCls}
            />
          </div>
          {!initial && (
            <div>
              <label className={labelCls}>Opening quantity</label>
              <input
                type="number" min={0} step="0.01"
                value={form.opening_quantity}
                onChange={(e) => setForm((f) => ({ ...f, opening_quantity: e.target.value }))}
                className={inputCls}
              />
              <p className="text-xs text-gray-500 mt-1">Posted as the item's opening receipt.</p>
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Description</label>
          <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inputCls} />
        </div>

        {initial && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4" />
            Active
          </label>
        )}

        {!initial && (
          <p className="text-xs text-gray-500">
            After this, the balance changes only through receipts, issues and adjustments.
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          {initial && (
            <button
              type="button"
              onClick={del}
              disabled={removing}
              className="px-3 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm hover:bg-rose-50 disabled:opacity-60"
              aria-label="Remove item"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="submit"
            disabled={creating || updating}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {(creating || updating) && <Loader2 className="w-4 h-4 animate-spin" />}
            {initial ? "Save changes" : "Add item"}
          </button>
        </div>
      </form>
    </StoreModal>
  );
}

/* ── Stock card ───────────────────────────────────────────────────────────── */

function StockCardModal({ item, onClose }: { item: any; onClose: () => void }) {
  const { data, isLoading } = useStoreGetItemQuery(item.id);
  const movements = data?.movements ?? [];

  return (
    <StoreModal title={`${item.name} — stock card`} icon={<Package className="w-4 h-4 text-indigo-600" />} onClose={onClose} wide>
      <div className="px-4 py-3 rounded-xl bg-gray-50 border text-sm">
        <div className="font-medium text-gray-900">
          {fmtQty(data?.quantity ?? item.quantity)} {item.unit} in stock
        </div>
        <div className="text-gray-500 mt-0.5 text-xs">
          <span className="font-mono">{item.code}</span>
          {item.category ? ` · ${item.category}` : ""} · reorder at {fmtQty(item.reorder_level)}
          {item.unit_cost ? ` · ${fmtNaira(item.unit_cost)} per ${item.unit}` : ""}
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center text-gray-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : movements.length === 0 ? (
        <div className="mt-4 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">No movements recorded yet.</div>
      ) : (
        <div className="mt-4 border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium text-right">Qty</th>
                <th className="px-3 py-2 font-medium text-right">Balance</th>
                <th className="px-3 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {movements.map((m: any) => (
                <tr key={m.id} className="hover:bg-gray-50/60">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(m.movement_date)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${movementColor(m.type)}`}>
                      {movementLabel(m.type)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-800">{signedQty(m)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmtQty(m.balance_after)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">
                    {m.type === "out" ? recipient(m) : m.supplier || m.note || "—"}
                    {m.reference ? <div className="text-gray-400">Ref: {m.reference}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </StoreModal>
  );
}
